import {TimelineMax, Sine, Power3} from 'gsap';
import {prepareObjectGeometry} from '../init/PrepareGeometry';
const THREE = require('three');

export const Factory = (params, geometryFactory) => {
    const _geometryFactory = geometryFactory;
    const _geometries = {};

    const _getGeometry = stateName => {
        if (_geometries[stateName]) return _geometries[stateName];

        const obj = _geometryFactory(stateName);
        if (!obj || !obj.geometry) return null;

        const geom = prepareObjectGeometry(obj.geometry, params.geometryScale);

        const geometry = {
            name: stateName,
            geometry: geom,
            position: obj.position,
            rotation: obj.rotation
        };

        // Store geometries:
        _geometries[stateName] = geometry;

        return geometry;
    };

    return (stateName, prevStateName = null) => {
        const state = {};

        // Core tunnel state definition:
        // console.log(stateName, prevStateName, `${stateName}-from-${prevStateName}`, `${prevStateName}-from-${stateName}`)
        let cameraTransition = params.camera.transitions[`${stateName}-from-${prevStateName}`];
        let transitionIsForth = true;

        if (!cameraTransition) {
            transitionIsForth = false;
            cameraTransition = params.camera.transitions[`${prevStateName}-from-${stateName}`];
        }
        if (!cameraTransition) {
            transitionIsForth = true;
            cameraTransition = params.camera.transitions[stateName];
        }

        if (!cameraTransition) return null;

        //
        // CAMERA:
        //

        const v0 = cameraTransition.positions[0];
        const v1 = cameraTransition.positions[1];
        const v2 = cameraTransition.positions[cameraTransition.positions.length - 1];
        const n0 = v1
            ? v1
                  .clone()
                  .sub(v0)
                  .normalize()
            : new THREE.Vector3(0, 0, -1);

        const curve0 = _createCubicBezierCurve(
            cameraTransition.positions,
            cameraTransition.roundRatio,
            cameraTransition.curveRatio,
            cameraTransition.roundMax,
            false
        );

        /* const curve1 = _createCubicBezierCurve(
            cameraTransition.positions,
            cameraTransition.roundRatio,
            cameraTransition.curveRatio,
            cameraTransition.roundMax,
            true,
            cameraTransition.finalNormal,
            params.camera.pointOfInterestZ0
        );*/

        const from = {
            position: v0 ? v0.clone() : null,
            // pOIPosition: v0.clone().add(n0.clone().multiplyScalar(params.camera.pointOfInterestZ0)),
            normal: n0,
            rotation: cameraTransition.rotations ? cameraTransition.rotations[0] || {x: 0, y: 0, z: 0} : {x: 0, y: 0, z: 0},
            addRotationZ: 0
        };

        const to = {
            position: v2.clone(),
            // pOIPosition: v2.clone().add(cameraTransition.finalNormal.clone().multiplyScalar(params.camera.pointOfInterestZ0)),
            normal: cameraTransition.finalNormal,
            rotation: cameraTransition.rotations ? cameraTransition.rotations[cameraTransition.rotations.length - 1] || {x: 0, y: 0, z: 0} : {x: 0, y: 0, z: 0},
            addRotationZ: 0
        };

        state.transitionIsForth = transitionIsForth;
        state.prevStateName = prevStateName;
        state.camera = {
            speed: cameraTransition.speed || params.camera.speed,
            transition: cameraTransition,
            positionCurve: curve0,
            // pOICurve: curve1,
            rotationTween: _getRotationTweenAlongCurve(cameraTransition.rotations, curve0),
            addRotationZTween: _getAdditionalRotationTweenAlongCurve(cameraTransition.round, curve0),
            from: transitionIsForth ? from : to,
            position: transitionIsForth ? to.position : from.position,
            // pOIPosition: transitionIsForth ? to.pOIPosition : from.pOIPosition,
            normal: transitionIsForth ? to.normal : from.normal,
            rotation: transitionIsForth ? to.rotation : from.rotation,
            addRotationZ: transitionIsForth ? to.addRotationZ : from.addRotationZ
        };

        if (state.camera.positionCurve) {
            state.camera.positionCurve.arcLengthDivisions = 2500;
            state.camera.positionCurve.updateArcLengths();
        }
        /* if (state.camera.pOICurve) {
            state.camera.pOICurve.arcLengthDivisions = 2500;
            state.camera.pOICurve.updateArcLengths();
        }*/
        //

        //
        // GEOMETRY
        //

        state.geometry = _getGeometry(stateName);
        if (!transitionIsForth) state.previousGeometry = _getGeometry(prevStateName);

        return state;
    };
};

export const ExtractTransitionInitialState = state => {
    return {
        camera: {
            transition: null,
            from: null,
            position: state.camera.from ? state.camera.from.position : state.camera.position,
            normal: state.camera.from ? state.camera.from.normal : state.camera.normal,
            rotation: state.camera.from ? state.camera.from.rotation : state.camera.rotation
        },
        geometry: {
            name: state.prevStateName
        }
    };
};
/*
const _createPointOfInterestCurve = (points, finalNormal, pointOfInterestZ0, roundRatio = 0.3, curveRatio = 0.7, roundMax = 50) => {
    const shiftedPoints = [];
    let i = 0;
    let p0;
    let p1;

    if (points.length > 1) {
        p0 = points[0];
        p1 = points[1];

        shiftedPoints.push(
            p0.clone().add(
                p1
                    .clone()
                    .sub(p0)
                    .setLength(pointOfInterestZ0)
            )
        );

        for (i = 1; i < points.length - 1; i++) {
            shiftedPoints.push(points[i].clone());
        }

        p0 = points[points.length - 2];
        p1 = points[points.length - 1];
        shiftedPoints.push(p1.clone().add((finalNormal || p1.clone().sub(p0)).setLength(pointOfInterestZ0)));

        return _createCubicBezierCurve(shiftedPoints, roundRatio, curveRatio, roundMax);
    }

    return _createCubicBezierCurve(points, roundRatio, curveRatio, roundMax);
};*/

const _shiftAlongVector = (p0, vec, value) => {
    return p0.clone().add(vec.setLength(value));
};

const _createCubicBezierCurve = (points, roundRatio = 0.3, curveRatio = 0.7, roundMax = 500, shift = false, finalNormal = null, pointOfInterestZ0 = 1) => {
    if (!points || points.length < 2) return null;

    const path = new THREE.CurvePath();

    let p0 = points[0];
    let p1 = points[0];
    let p2 = points[1];

    if (points.length < 3) {
        path.add(
            shift
                ? new THREE.LineCurve3(
                      _shiftAlongVector(p0, p2.clone().sub(p0), pointOfInterestZ0),
                      _shiftAlongVector(p2, p2.clone().sub(p0), pointOfInterestZ0)
                  )
                : new THREE.LineCurve3(p0, p2)
        );
        return path;
    }

    let pHalf = p1.clone().add(
        p2
            .clone()
            .sub(p1)
            .multiplyScalar(0.5)
    );
    path.add(new THREE.LineCurve3(shift ? _shiftAlongVector(p0, p2.clone().sub(p0), pointOfInterestZ0) : p0, pHalf));

    let i = 1;
    let v0;
    let v1;
    let l0;
    let l1;
    let r0;
    let r1;
    let c0;
    let c1;

    while (i < points.length - 1) {
        p0 = pHalf;
        p1 = p2;
        p2 = points[i + 1];
        pHalf = p1.clone().add(
            p2
                .clone()
                .sub(p1)
                .multiplyScalar(0.5)
        );

        v0 = p1.clone().sub(p0);
        v1 = p1.clone().sub(pHalf);
        l0 = v0.length();
        l1 = v1.length();
        r0 = Math.min(l0 * roundRatio, roundMax);
        r1 = Math.min(l1 * roundRatio, roundMax);
        c0 = p0.clone().add(v0.multiplyScalar((l0 - r0) / l0));
        c1 = pHalf.clone().add(v1.multiplyScalar((l1 - r1) / l1));

        path.add(new THREE.LineCurve3(p0, c0));
        path.add(
            new THREE.CubicBezierCurve3(
                c0,
                c0.clone().add(v0.multiplyScalar((r0 * curveRatio) / l0)),
                c1.clone().add(v1.multiplyScalar((r1 * curveRatio) / l1)),
                c1
            )
        );

        v0 = p2
            .clone()
            .sub(p1)
            .setLength(r1 * curveRatio);

        if (i + 1 < points.length - 1) {
            // Points inside
            path.add(new THREE.LineCurve3(c1, pHalf));
        } else {
            // Final points
            // - Shift
            if (shift) {
                p0 = _shiftAlongVector(p2, finalNormal, pointOfInterestZ0);

                if (v0.normalize().equals(finalNormal.normalize())) {
                    // - Shift without rotation
                    path.add(new THREE.LineCurve3(c1, p0));
                } else {
                    // - Shift with rotation
                    path.add(new THREE.LineCurve3(c1, p2));
                    path.add(
                        //
                        new THREE.CubicBezierCurve3(
                            //
                            p2,
                            v0,
                            v0.clone().negate(),
                            p0
                        )
                    );
                }
            } else {
                // - No shift
                path.add(new THREE.LineCurve3(c1, i + 1 < points.length - 1 ? pHalf : p2));
            }
            //
        }

        i++;
    }

    return path;
};

const _getRotationTweenAlongCurve = (rArr, path0 /* , path1*/) => {
    if (!rArr || rArr.length < 2) return null;
    const tl = new TimelineMax({paused: true});

    let i;
    let c0;
    // let c1;
    let c2;
    const totalL = path0.getLength();
    let curL0 = 0;
    let lastModeOn = false;
    let curLDelta = 0;
    let curLDeltaN = 0;
    let prevL = 0;
    // let curL1 = 0;
    let n = 0;
    let r0 = rArr[n];
    let r1 = r0;
    const tgt = {
        rotation: 0
    };
    tl.set(
        tgt,
        {
            x: r0.x,
            y: r0.y,
            z: r0.z
        },
        'st'
    );

    n++;

    for (i = 0; i < path0.curves.length; i++) {
        c0 = path0.curves[i];
        // c1 = path1.curves[i];
        c2 = i < path0.curves.length - 1 ? path0.curves[i + 1] : null;

        if (lastModeOn) {
            if (c0.isCubicBezierCurve3) curLDeltaN--;
            curLDelta += c0.getLength();

            if (curLDeltaN > 0) continue;
        }

        if (!lastModeOn && !c0.isCubicBezierCurve3) {
            curL0 += c0.getLength();
            prevL = c0.getLength();
            // curL1 += c1.getLength();
            continue;
        }

        r1 = rArr[n];
        r1 = r1 || r0;

        if (r1 === r0) {
            curL0 += c0.getLength();
            prevL = c0.getLength();
            // curL1 += c1.getLength();
            n++;
            continue;
        }

        if (!lastModeOn) {
            curLDelta = c0.getLength();

            // Start count down:
            if (r1.mode && r1.mode === 'last') {
                lastModeOn = true;
                curLDeltaN = r1.count - 1;
                continue;
            }
        }

        if (r1 !== r0) {
            tl.to(
                tgt,
                (curLDelta + Math.min(100, prevL * 0.5) + (c2 ? Math.min(100, c2.getLength() * 0.5) : 0)) / totalL,
                {
                    x: r1.x,
                    y: r1.y,
                    z: r1.z,
                    ease: Sine.easeInOut
                },
                'st+=' + (curL0 - Math.min(100, prevL * 0.5)) / totalL
            );
            r0 = r1;
        }

        curL0 += curLDelta;
        prevL = curLDelta;
        // curL1 += c1.getLength();
        lastModeOn = false;
        curLDelta = 0;
        n++;
    }
    tl.set(
        tgt,
        {
            x: r0.x,
            y: r0.y,
            z: r0.z
        },
        'st+=' + 1
    );

    return {timeline: tl, object: tgt};
};

const _getAdditionalRotationTweenAlongCurve = (numArr, path0 /* , path1*/) => {
    if (!numArr || numArr.length < 2) return null;
    const tl = new TimelineMax({paused: true});

    let i;
    let c0;
    // let c1;
    // let c2;
    const totalL = path0.getLength();
    let curL0 = 0;
    let curLDelta = 0;
    let c0L;
    // let prevL = 0;
    // let curL1 = 0;
    let n = 0;
    let num;
    const tgt = {
        rotation: 0
    };
    tl.set(
        tgt,
        {
            z: 0
        },
        'st'
    );

    for (i = 0; i < path0.curves.length; i++) {
        c0 = path0.curves[i];
        // c1 = path1.curves[i];
        // c2 = i < path0.curves.length - 1 ? path0.curves[i + 1] : null;

        c0L = c0.getLength();

        if (!c0.isCubicBezierCurve3) {
            curLDelta += c0L;
        } else {
            num = numArr[n] || 0;

            if (num !== 0) {
                tl.to(
                    tgt,
                    curLDelta / totalL,
                    {
                        z: num * 360,
                        ease: Power3.easeInOut
                    },
                    'st+=' + (curL0 + Math.min(50, curLDelta * 0.1)) / totalL
                );
            }

            curL0 += curLDelta;
            curL0 += c0L;
            curLDelta = 0;
            n++;
        }
    }
    tl.set(
        tgt,
        {
            z: 0
        },
        'st+=' + 1
    );

    return {timeline: tl, object: tgt};
};
